import { useMemo, useCallback, useState } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { Typography } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import type { TextComponentSpec } from "../../Processing/types";
import { useSlicerVariables } from "../../Contexts/dashboardHooks";
import { resolveTemplate, saveBlockingWarnings } from "../../Utils/resolveTemplate";
import { isMalformedSlicerWarning } from "../../Utils/resolveTemplate.mjs";
import { RichTextEditor } from "./RichTextEditor";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import {
  textReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  type TextFormState,
} from "./textState";

const { Text } = Typography;


export function TextConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<TextComponentSpec>) {
  const slicerValues = useSlicerVariables();
  const toSpec = useCallback(
    (s: TextFormState) =>
      saveBlockingWarnings(s.content, slicerValues).length > 0 ? null : formStateToSpec(s),
    [slicerValues],
  );
  const [state, dispatch] = useConfiguratorSync({
    reducer: textReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  const { warnings } = useMemo(
    () => resolveTemplate(state.content, slicerValues, { escapeHtml: true }),
    [state.content, slicerValues],
  );
  // The coach is in the middle of the token while the `{Slicer.` menu is open, so the
  // brace-scan warning waits; Save stays gated by `saveBlockingWarnings` in `toSpec`.
  const [menuOpen, setMenuOpen] = useState(false);
  const shown = menuOpen ? warnings.filter((w) => !isMalformedSlicerWarning(w)) : warnings;

  return (
    <div className="awm-text-configurator">
      <Text strong style={{ fontSize: 13 }}>Content</Text>
      <RichTextEditor
        value={state.content}
        onChange={(html) => dispatch({ type: "SET_CONTENT", content: html })}
        onMenuOpenChange={setMenuOpen}
      />
      <Text type="secondary" style={{ fontSize: 11 }}>
        Format text with the toolbar. Type {"{Slicer."} to quote a slicer, or insert variables from the Variables menu, then use Preview to see them resolved.
      </Text>
      {shown.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
          {shown.map((w, i) => (
            <Text key={i} type="warning" style={{ fontSize: 12 }}>
              <WarningOutlined style={{ marginRight: 4 }} />
              {w}
            </Text>
          ))}
        </div>
      )}
    </div>
  );
}
